import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserCheck } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import FilterPopover from "../components/FilterPopover";
import { useSettings, formatMoney } from "../lib/settings";

const statusColor = (s) =>
  s === "active" ? "var(--pg-accent-deep)" :
  s === "paused" ? "var(--pg-yellow)" :
  s === "ended" ? "var(--pg-text-mute)" : "var(--pg-text-dim)";

export default function Clients() {
  const navigate = useNavigate();
  const { currency } = useSettings();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterState, setFilterState] = useState({ status: [] });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } catch { toast.error("Failed to load clients"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let rows = clients;
    if (filterState.status.length > 0) {
      const set = new Set(filterState.status);
      rows = rows.filter((c) => set.has(c.status || "active"));
    }
    return rows;
  }, [clients, filterState]);

  return (
    <div data-testid="clients-page">
      <PageHeader title="Clients" subtitle={`${clients.length} active engagements`} />

      <div className="px-8 pt-4 pb-2 flex items-center gap-2">
        <FilterPopover
          testid="clients-filter"
          matchCount={filtered.length}
          value={filterState}
          onChange={setFilterState}
          groups={[
            {
              key: "status",
              title: "Status",
              options: [
                { value: "active", label: "Active" },
                { value: "paused", label: "Paused" },
                { value: "ended",  label: "Ended" },
              ],
            },
          ]}
        />
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="px-8 py-20 text-center text-mute mono text-xs">loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-8 py-12">
            <div className="empty-state">
              <UserCheck size={28} className="text-mute mb-3 mx-auto" />
              <div className="text-lg mb-2" style={{ color: "var(--pg-text)" }}>No clients yet</div>
              <div className="mono text-xs">When you close a Deal as Won, the associated Company becomes a Client.</div>
            </div>
          </div>
        ) : (
          <table className="pg-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Engagements</th>
                <th>Deliverables</th>
                <th>Total Value</th>
                <th>Status</th>
                <th>Since</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-row-${c.id}`}>
                  <td style={{ color: "var(--pg-text)" }}>{c.company_name || "—"}</td>
                  <td className="mono text-xs text-dim">{c.deal_count || 0} deal{c.deal_count === 1 ? "" : "s"}</td>
                  <td className="mono text-xs text-dim">{c.deliverable_count || 0}</td>
                  <td className="mono text-xs" style={{ color: "var(--pg-text)" }}>{formatMoney(c.total_value, currency.symbol)}</td>
                  <td>
                    <span className="mono text-[11px] uppercase" style={{ color: statusColor(c.status) }}>{c.status || "active"}</span>
                  </td>
                  <td className="mono text-xs text-dim">{(c.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
